import styles from './Table.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
// import Bag from '@assets/icons/bag.svg';
import BeforeAwaitingTable from './BeforeAwaitingTable';
import Box from '@components/Box/Box';
import DataTable, { BeforeProps } from "@containers/DataTable";
import Fire from '@assets/icons/fire.svg';
import Flag from '@components/Flag';
import InfoIcon from '@assets/icons/info-grey-18.svg';
// import Popup from "@components/Popup";
import ProgressBar from "@components/ProgressBar";
import TableSelectionPanel from "@components/Table/TableSelectionPanel";
import TrackingNumber from "@components/TrackingNumber";
import WarningIcon from '@assets/icons/warning-red-12.svg';
import { Button, ButtonSize, ButtonType } from "@components/Button";
import { createSumFromObject, formatSumFromObject, formatSumWithCurrency } from '@utils/formatValues';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { getCurrentLanguage } from '@utils/lang';
import { getDaysBetweenDates, getProgressFromTo, getProgressTo } from '@utils/progress';
import { getReturnsPage, Return, ReturnItem, ReturnTypes, TCountryTariff, TCountryTariffs } from '@api/apiClient';
import { ReactElement, useCallback, useMemo } from 'react';
import { TableColumn, TableColumnType, TableRowType } from "@components/Table/TableTypes";
import { Text, TextColor, TextSize, TextType, TextWeight } from '@components/Text';
import { Tooltip } from '@components/new/Tooltip';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from "react-i18next";

interface AwaitingTableProps {
  dataGetCountryTariffs: TCountryTariffs;
  onClickDisposal: () => void;
  onClickDeliver: () => void;
  onSelectionChange: (ids: string[], data: Return[]) => void;
  onClickResale: (row: Return) => void;
  onClickPhoneConfirm: () => void;
  onParcelClick: (parcelId: string) => void;
  pending?: boolean,
  filters: FilterModel[],
  selectedRowIds: string[],
  selectedDelivery: number;
  selectedSum: number;
  selectedDepartureCount: number;
  downloadingBtn: ReactElement;
  hasExtraSmallOnly: boolean;
  hasExtraSmall: boolean;
}

const AwaitingTable = ({
  dataGetCountryTariffs,
  // onClickPhoneConfirm,
  // onClickResale,
  selectedRowIds,
  onClickDisposal,
  onClickDeliver,
  onParcelClick,
  pending,
  onSelectionChange,
  filters,
  selectedDelivery,
  selectedSum,
  selectedDepartureCount,
  downloadingBtn,
  hasExtraSmallOnly,
  hasExtraSmall,
}: AwaitingTableProps) => {
  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { t } = useTranslation();

  const isPhoneConfirmed = account?.isPhoneConfirmed || (account?.region !== "CN");
  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const BeforeAwaitingTableComponent = useCallback((p: BeforeProps<Return>) => {
    return (
      <BeforeAwaitingTable {...p} />
    );
  }, []);

  // const onResellClick = (row: TableRowType<Return>) => {
  //   isPhoneConfirmed
  //     ? onClickResale(row.data)
  //     : onClickPhoneConfirm();
  // }

  const createTariffsTooltip = (countryTariffs: TCountryTariff[]): string => {
    if (!countryTariffs) return '';
    const condition: boolean = account?.region === 'TR';
    const configs: { country: string, marketplace: string }[] =
        condition
            ? [
              { country: 'TR', marketplace: 'Ozon' },
              { country: 'TR', marketplace: 'Yandex' }
            ]
            : [
              { country: 'CN', marketplace: 'Ozon' },
              { country: 'HK', marketplace: 'Ozon' },
              { country: 'CN', marketplace: 'Yandex' },
              { country: 'HK', marketplace: 'Yandex' }
            ];
    return configs
        .map((item: { country: string, marketplace: string }) => {
          const { marketplace, country } = item;
          const tariff = condition
              ? countryTariffs.find(
                  (tariff: TCountryTariff) =>
                      tariff.operationType === 'ReturnToSeller' &&
                      tariff.country === country
              )
              : countryTariffs.find(
                  (tariff: TCountryTariff) =>
                      tariff.operationType === 'ReturnToSeller' &&
                      tariff.marketplace?.marketplaceName === marketplace &&
                      tariff.country === country
              );
          if (!tariff) return '';
          return `${marketplace} ${t(`countries.${tariff.country}`)} — ${createSumFromObject(tariff.cost)}`;
        })
        .filter(Boolean)
        .join('\n');
  };

  const columns: TableColumn<Return>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<Return>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => onParcelClick(row.data?.id)}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t("awaitingTable.attachment"),
      accessor: (row: TableRowType<Return>) => {
        const firstItem = row.data.items?.[0];
        return (
          <>
            {firstItem ? (
              <a href={firstItem.url ?? ""} target="_blank" rel="noreferrer">
                {firstItem.name ?? ""}
              </a>
            ) : null}
            <div className={styles.qualityTypes}>
              {(row.data.qualityType === "Damaged") ? (
                <Text
                  size={TextSize.XS}
                  color={TextColor.WHITE}
                  className={styles.qualityType}
                >
                  {t("awaitingTable.qualityTypeDamaged")}
                </Text>
              ) : null}
              {((account?.region !== "TR") && row.data.isExtraSmall) ? (
                <Text
                  size={TextSize.XXS}
                  color={TextColor.ERROR}
                  weight={TextWeight.SEMIBOLD}
                  className={styles.qualityType}
                >
                  {t("awaitingTable.notReturnable")}
                </Text>
              ) : null}
            </div>
          </>
        );
      },
      width: "235px"
    },
    {
      header: t("awaitingTable.quantity"),
      accessor: (row: TableRowType<Return>) => row.data.items.length ? row.data.items.reduce((accumulator: number, item: ReturnItem) => accumulator + item.quantity, 0) : '',
      alignment: "left",
      type: TableColumnType.TERTIARY
    },
    {
      header: t("awaitingTable.service"),
      accessor: (row: TableRowType<Return>) => row.data.serviceName,
      width: "200px"
    },
    {
      header: t("awaitingTable.value"),
      accessor: (row: TableRowType<Return>) => (
        <Text
            color={TextColor.PRIMARY}
            size={TextSize.S}
        >
          {formatSumFromObject(row.data?.declaredValue)}
        </Text>
      ),
      alignment: "right",
      width: "120px"
    },
    {
      header: (
        <Box flex="row">
          {t("awaitingTable.delivery_cost")}
          {" "}
          {dataGetCountryTariffs && (
              <Tooltip
                  action={
                    <img
                        alt="Info"
                        src={InfoIcon}
                    />
                  }
                  text={createTariffsTooltip(dataGetCountryTariffs.countryTariffs)}
                  position="top"
                  whiteSpace="pre-wrap"
              />
          )}
        </Box>
      ),
      accessor: (row: TableRowType<Return>) => (
          <div className={styles.actionPrice}>
            {account?.region === 'TR' ? (
                <div className={styles.actionPriceItem}>
                  <Flag countryCode={row.data.countryCode}/>
                  {formatSumFromObject(row.data.deliveryPrice)}
                </div>
            ) : (
                <>
                  {row.data.isExtraSmall ? (
                      <Tooltip
                          action={
                            <Text
                                color={TextColor.WARM_PINK}
                                size={TextSize.XS}
                                weight={TextWeight.SEMIBOLD}
                            >
                              {t('awaitingTable.no')}
                            </Text>
                          }
                          position="top"
                          text={t('returnModal.order.forbiddenExtraSmallMessage')}
                      />
                  ) : (
                      <>
                        <div className={styles.actionPriceItem}>
                          <Flag countryCode={row.data.countryCode}/>
                          {formatSumFromObject(row.data.deliveryPrice)}
                        </div>
                        <div className={styles.actionPriceItem}>
                          <Flag countryCode="RU"/>
                          <Text
                              color={TextColor.DARK_MUTED}
                              size={TextSize.XS}
                          >
                            {formatSumFromObject(row.data.russiaDeliveryPrice)}
                          </Text>
                        </div>
                      </>
                  )}
                </>
            )}
          </div>
      ),
      type: TableColumnType.PRIMARY,
      width: '100px',
      noOverflow: true
    },
    {
      header: t("awaitingTable.seller"),
      accessor: (row: TableRowType<Return>) => (
        <AvatarWithMarketplace
          name={row.data.sellerName}
          id={row.data.sellerId}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t("awaitingTable.until_disposal"),
      accessor: (row: TableRowType<Return>) => {
        const restDays = getDaysBetweenDates(null, row.data.estimatedUtilizationDate);
        if (row.data.marketplaceType === "Ozon" && account?.region === "CN") {
          return (
            <ProgressBar
              colors={[
                { value: 899/14, main: "#5ec983", bg: "#ceefda", label: "#5ec983" },
                { value: 599/14, main: "#f2af4c", bg: "#fcefdb", label: "#f2af4c" },
                { value: 0, main: "#ee6373", bg: "#fce0e3", label: "#ee6373" },
              ]}
              progress={100 - getProgressTo(row.data.estimatedUtilizationDate, 14)}
              label={restDays ? `${restDays} ${t("awaitingTable.days")}` : t("awaitingTable.lastDay")}
              column
            />
          )
        }
        return (
          <ProgressBar
            reverseColors
            progress={getProgressFromTo(row.data.arrivedToDropOffAt, row.data.estimatedUtilizationDate, true)}
            label={restDays ? `${restDays} ${t("awaitingTable.days")}` : t("awaitingTable.lastDay")}
            column
          />
        )
      }
    },
    // {
    //   header: "",
    //   accessor: (row: TableRowType<Return>) => (
    //     <>
    //       {(
    //         row.data.hasLinkedResales
    //         && !row.data.isDeparture
    //       ) ? (
    //         <Button
    //           icon={Bag}
    //           onClick={() => onResellClick(row)}
    //           size={ButtonSize.XS}
    //           isRounded
    //           title={t("awaitingTable.resellButton")}
    //         />
    //       ) : (
    //         <Popup text={t("awaitingTable.notResale")}>
    //           <Button
    //             icon={Bag}
    //             disabled
    //             onClick={() => {}}
    //             size={ButtonSize.XS}
    //             isRounded
    //             title={t("awaitingTable.resellButton")}
    //           />
    //         </Popup>
    //       )}
    //     </>
    //   ),
    //   alignment: 'center',
    //   noOverflow: true
    // },
  ], [dataGetCountryTariffs, isPhoneConfirmed, currentLanguage]);

  return (
    <TableSelectionPanel
      isShown={selectedRowIds.length > 0}
      items={[
        {
          label: t("awaitingTableSelectionPanel.selected"),
          value: selectedRowIds.length,
        },
        {
          label: t("awaitingTableSelectionPanel.deliveryCost"),
          value: hasExtraSmallOnly ? "-" : createSumFromObject({ amount: selectedSum, currency: 'RUB' }),
        },
      ]}
      message={hasExtraSmall ? (
        <span style={{ maxWidth: "145px" }}>
          <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
            {t("awaitingTable.extraSmallMessage")}
          </Text>
        </span>
      ) : null}
      buttons={[
        (
          <Tooltip
            action={<img alt="Info" src={InfoIcon} />}
            key="tooltip"
            position="top"
            text={(
              <>
                <Box pb={4} flex="column">
                  <Text size={TextSize.S} color={TextColor.WHITE} weight={TextWeight.BOLD}>
                    {t("awaitingTable.tooltip.title1")}
                  </Text>
                  <Text size={TextSize.XS} color={TextColor.WHITE}>
                    {t("awaitingTable.tooltip.text1")}
                  </Text>
                </Box>
                <Box flex="column">
                  <Text size={TextSize.S} color={TextColor.WHITE} weight={TextWeight.BOLD}>
                    {t("awaitingTable.tooltip.title2")}
                  </Text>
                  <Text size={TextSize.XS} color={TextColor.WHITE}>
                    {t("awaitingTable.tooltip.text2")}
                  </Text>
                </Box>
              </>
            )}
          />
        ),
        downloadingBtn,
        (<Button
          key="disposal"
          onClick={onClickDisposal}
          size={ButtonSize.S}
          icon={Fire}
          type={ButtonType.DANGER}
          title={t("awaitingTableSelectionPanel.to_recycle")}
          disabled={selectedDepartureCount > 0}
        />),
        hasExtraSmallOnly ? null : (
            <Button
                key="deliver"
                onClick={onClickDeliver}
                size={ButtonSize.S}
                title={
                  account?.region === 'TR'
                      ? t('awaitingTableSelectionPanel.to_return') + ` ${formatSumWithCurrency(selectedDelivery, variableCurrency)}`
                      : t('awaitingTableSelectionPanel.return')
                }
            />
        )
      ]}
      footer={(selectedDepartureCount) ? (
          <>
            {selectedDepartureCount ? (
                <>
                  <div className={styles.panelFooter}>
                    <Text
                        size={TextSize.M}
                        type={TextType.BLOCK}
                        color={TextColor.ERROR_DARK}
                    >
                      <img
                          alt="Warning"
                          className={styles.warning}
                          src={WarningIcon}
                      />
                      {t('awaitingPage.await_departure1')}
                    </Text>
                  </div>
                  {selectedRowIds.length > selectedDepartureCount && (
                      <div className={styles.panelFooter}>
                        <Text
                            size={TextSize.M}
                            type={TextType.BLOCK}
                            color={TextColor.ERROR_DARK}
                        >
                          <img
                              alt="Warning"
                              className={styles.warning}
                              src={WarningIcon}
                          />
                          {t('awaitingPage.await_departure2')}
                        </Text>
                      </div>
                  )}
                </>
            ) : null}
          </>
      ) : null}
    >
      <DataTable<Return>
        Before={BeforeAwaitingTableComponent}
        queryKey="returns-page"
        pending={pending}
        getData={getReturnsPage}
        columns={columns}
        selectable
        selectedIds={selectedRowIds}
        onSelectionChange={onSelectionChange}
        notFound={t("awaitingPage.not_await")}
        filters={[
          ...filters,
          { name: "ReturnTypes", value: [ReturnTypes.NotSelected] },
        ]}
        rowClassName={(row) => ((row.isSelected && row.data?.isDeparture) ? styles.extraSmallParcelRow : "")}
      />
    </TableSelectionPanel>
  );
};

export default AwaitingTable;
